import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cx } from '@/lib/cx';
import { Icon, type IconName } from './Icon';
import { Spinner } from './Spinner';

/**
 * Buttons, reproducing v1's recurring shapes.
 *
 * v1 primary CTA (~20 stylesheets, e.g. AcademicSessions.css `__save-btn`):
 * `padding: 9px 22px; border-radius: 8px; font-weight: 600;
 * background: linear-gradient(135deg, #667eea 0%, #764ba2 100%)`.
 *
 * v1 `.students__print-btn` (secondary): the same metrics with a transparent
 * background, a `1px solid rgba(0,0,0,0.12)` border and `#6b7280` text.
 *
 * v1 also had a solid `#7c3aed` variant (`.students__add-btn`) that never got
 * reconciled with the gradient one. `accent` preserves it for the screens that
 * used it; new screens should use `primary`.
 *
 * Variants encode intent, not appearance. Pick by what the button *does*:
 *
 *   primary   — the one action this screen exists for. At most one per pane.
 *   secondary — a real action, but not the point of the screen.
 *   ghost     — toolbar and table-row actions; no chrome until hovered.
 *   danger    — destroys data.
 *
 * There is no `success` variant on purpose. Green means "this record is in a
 * good state", never "click here" — a green button trains staff to read colour
 * as decoration, which is how a red Delete stops registering as a warning.
 */
type Variant = 'primary' | 'accent' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md';

const variants: Record<Variant, string> = {
  primary:
    'border-transparent bg-linear-to-br from-brand-from to-brand-to text-on-accent hover:opacity-90',
  accent: 'border-transparent bg-accent text-on-accent hover:bg-accent-hover',
  secondary: 'border-line-strong bg-transparent text-muted hover:bg-subtle hover:text-ink',
  ghost: 'border-transparent bg-transparent text-muted hover:bg-subtle hover:text-ink',
  danger: 'border-danger-line bg-danger-soft text-danger hover:bg-danger hover:text-on-accent',
};

const sizes: Record<Size, string> = {
  sm: 'h-7 gap-1.5 px-3 text-2xs',
  md: 'h-8 gap-1.5 px-4 text-xs',
};

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: Variant;
  size?: Size;
  /** Leading icon. Use `iconOnly` when there is no label. */
  icon?: IconName;
  /** Square button showing only the icon. Requires `aria-label`. */
  iconOnly?: boolean;
  /** Swaps the icon for a spinner and blocks interaction. */
  loading?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', icon, iconOnly, loading, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cx(
        'inline-flex items-center justify-center whitespace-nowrap rounded-md border font-medium',
        'transition-colors duration-100',
        'disabled:pointer-events-none disabled:opacity-45',
        sizes[size],
        iconOnly && (size === 'sm' ? 'w-7 px-0' : 'w-8 px-0'),
        variants[variant],
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size={size === 'sm' ? 12 : 14} /> : icon ? <Icon name={icon} size={size === 'sm' ? 13 : 15} /> : null}
      {!iconOnly && children}
    </button>
  );
});
